import { cn } from "@marketing-sites/utils/cn";
import { Collapsible } from "@base-ui/react/collapsible";
import { ChevronDownIcon } from "../../icons/chevron-down-icon";
import type { NavItem, NavLinkSection } from "../../types";
import { useHeaderContext } from "../../context";
import { HeaderLink } from "../header-link";
import { Text } from "../text";
import { Button } from "../button";

type MobileNavItemProps = {
  item: NavItem;
  className?: string;
};

// Helper: Renders a list of dropdown links
const DropdownLinks = ({ links }: { links: NavLinkSection[][] }) => (
  <ul className="flex flex-col gap-1 pl-4 pt-2">
    {links.map((sections) =>
      sections.map((section) => (
        <li key={section.title}>
          {section.title && (
            <div className="text-nav-content-secondary px-2 py-1 text-xs font-semibold">
              {section.title}
            </div>
          )}
          <ul className="flex flex-col gap-1">
            {section.links.map((link, linkIndex) => {
              if ("columns" in link) return null;
              // prose / fullWidthRow are floating-variant only.
              if ("prose" in link || "fullWidthRow" in link) return null;
              return (
                <li key={linkIndex}>
                  <HeaderLink
                    // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
                    analyticsId={`${link.analyticsId}_mobile`}
                    href={link.href}
                    text={link.title}
                    analyticsText={link.analyticsText}
                    className="hover:bg-nav-hover block rounded-md p-2"
                  >
                    <div className="text-sm font-medium">{link.title}</div>
                    <div className="text-nav-content-tertiary text-xs">
                      {link.description}
                    </div>
                  </HeaderLink>
                </li>
              );
            })}
          </ul>
        </li>
      )),
    )}
  </ul>
);

// Custom item with dropdown
const CustomItemWithDropdown = ({
  item,
}: {
  item: Extract<NavItem, { type: "customWithDropdown" }>;
}) => (
  <Collapsible.Root>
    <Collapsible.Trigger className={cn("hover:bg-nav-hover rounded-md")}>
      {item.customItem}
    </Collapsible.Trigger>
    <Collapsible.Panel className="overflow-hidden transition-all data-[ending-style]:h-0 data-[starting-style]:h-0">
      <DropdownLinks links={item.links} />
    </Collapsible.Panel>
  </Collapsible.Root>
);

// Icon button — no tooltip on mobile, just the icon link
const IconButtonItem = ({
  item,
}: {
  item: Extract<NavItem, { type: "iconButton" }>;
}) => (
  <HeaderLink
    analyticsId={item.analyticsId}
    href={item.href}
    text={item.label}
    analyticsText={item.analyticsText}
    className="hover:bg-nav-hover flex h-10 w-10 items-center rounded-md"
  >
    {item.icon}
  </HeaderLink>
);

// Custom item without dropdown
const CustomItemSimple = ({
  item,
}: {
  item: Extract<NavItem, { type: "custom" }>;
}) => item.customItem;

// Standard item with dropdown (links or custom submenu)
const StandardItemWithDropdown = ({
  item,
}: {
  item: Extract<NavItem, { type: "dropdown" | "customDropdown" }>;
}) => (
  <Collapsible.Root>
    <Collapsible.Trigger
      className={cn(
        "flex w-full items-center justify-between",
        "hover:bg-nav-hover rounded-md p-2",
      )}
    >
      <Text
        variant="btnLg"
        className="flex w-full items-center justify-between"
      >
        {item.label}
        <ChevronDownIcon className="data-panel-open:rotate-180 transition-transform" />
      </Text>
    </Collapsible.Trigger>

    <Collapsible.Panel className="overflow-hidden transition-all data-[ending-style]:h-0 data-[starting-style]:h-0">
      {item.type === "dropdown" ? (
        <DropdownLinks links={item.links} />
      ) : (
        item.customSubmenu
      )}
    </Collapsible.Panel>
  </Collapsible.Root>
);

// Button item
const ButtonItem = ({
  item,
}: {
  item: Extract<NavItem, { type: "button" }>;
}) => {
  const { variant } = useHeaderContext();
  const floating = variant === "floating";
  return (
    <Button
      variant={item.variant ?? "primary"}
      size={item.size ?? "base"}
      isExternal={floating ? false : item.isExternal}
      className={cn(
        !floating && "ml-2 [li:first-child:has(>&)_&]:ml-0",
        floating &&
          "h-10 gap-1 rounded-[4px] px-3 py-0 text-[14px] leading-[1.1] min-[1140px]:px-3 xl:px-3 xl:py-0 xl:text-[14px]",
        item.className,
      )}
      render={
        <HeaderLink
          analyticsId={item.analyticsId}
          href={item.href}
          text={item.label}
          analyticsText={item.analyticsText}
        />
      }
    >
      {floating ? (
        <>
          {item.label}
          {item.isExternal && <span aria-hidden>→</span>}
        </>
      ) : (
        item.label
      )}
    </Button>
  );
};

// Standard item without dropdown (simple link)
const StandardItemSimpleLink = ({
  item,
  className,
}: {
  item: Extract<NavItem, { type: "link" }>;
  className?: string;
}) => {
  const { variant } = useHeaderContext();
  const floating = variant === "floating";
  return (
    <HeaderLink
      analyticsId={item.analyticsId}
      href={item.href}
      text={item.label}
      analyticsText={item.analyticsText}
      className={cn(
        "hover:bg-nav-hover flex h-10 items-center rounded-md p-2 text-center",
        className,
      )}
    >
      <Text
        variant={floating ? undefined : "btnLg"}
        className={cn(floating && "text-[14px] leading-[1.1]")}
      >
        {item.label}
      </Text>
    </HeaderLink>
  );
};

export const MobileNavItem = ({ item, className }: MobileNavItemProps) => {
  switch (item.type) {
    case "iconButton":
      return <IconButtonItem item={item} />;
    case "customWithDropdown":
      return <CustomItemWithDropdown item={item} />;
    case "custom":
      return <CustomItemSimple item={item} />;
    case "dropdown":
    case "customDropdown":
      return <StandardItemWithDropdown item={item} />;
    case "link":
      return <StandardItemSimpleLink item={item} className={className} />;
    case "button":
      return <ButtonItem item={item} />;
  }
};
